import { Component, EventEmitter, Inject, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { 
    ConfirmationModalType, 
    ConfirmationModalTypeConfig, 
    CONFIRMATION_MODAL_TYPE_CONFIGS 
} from './confirmation-modal.types';

/**
 * Data interface for the confirmation modal
 * Defines all configurable options of the modal
 */
export interface ConfirmationModalData {
    /** Modal title (required) */
    title: string;

    /** Modal description or message (required, supports HTML) */
    description: string;

    /** 
     * Modal type (success, warning, danger, info, default)
     * Automatically configures colors for icon, text, and button
     * @default ConfirmationModalType.DEFAULT
     */
    type?: ConfirmationModalType;

    /** 
     * Custom text color (optional, overrides type color)
     * @deprecated Use 'type' property for consistency
     */
    descriptionColor?: string;

    /** Optional icon to display (Material Icon name or TemplateRef) */
    descriptionIcon?: string | TemplateRef<any>;

    /** Confirm button text (optional, default: 'Confirmar') */
    confirmButtonText?: string;

    /** Cancel button text (optional, default: 'Cancelar') */
    cancelButtonText?: string;

    /** 
     * Danger mode: changes button color to 'warn' (red)
     * @deprecated Use type: ConfirmationModalType.DANGER instead
     */
    dangerMode?: boolean;

    /** Custom modal width (optional, default: '400px') */
    customWidth?: string | number;
}

/**
 * ConfirmationModalComponent
 *
 * Reusable component for displaying generic confirmation modals.
 * Centralizes all system confirmation dialogs (delete, void, execute critical actions).
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(ConfirmationModalComponent, {
 *   width: '400px',
 *   data: {
 *     title: 'Delete record',
 *     description: 'Are you sure you want to delete this item?',
 *     dangerMode: true,
 *     confirmButtonText: 'Delete',
 *     descriptionIcon: 'delete'
 *   }
 * });
 *
 * dialogRef.afterClosed().subscribe(result => {
 *   if (result) {
 *     this.deleteItem();
 *   }
 * });
 * ```
 *
 * @standalone
 */
@Component({
    selector: 'app-confirmation-modal',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule
    ],
    templateUrl: './confirmation-modal.component.html',
    styleUrls: ['./confirmation-modal.component.scss']
})
export class ConfirmationModalComponent {
    /** Event emitted when the user confirms the action */
    @Output() onConfirm = new EventEmitter<void>();

    // Properties processed from the received data
    title: string;
    description: string;
    descriptionColor: string;
    descriptionIcon?: string | TemplateRef<any>;
    confirmButtonText: string;
    cancelButtonText: string;
    
    // Type-based configuration
    modalType: ConfirmationModalType;
    typeConfig: ConfirmationModalTypeConfig;
    buttonColor: 'primary' | 'accent' | 'warn';
    iconBackgroundColor: string;
    iconColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;

    constructor(
        public dialogRef: MatDialogRef<ConfirmationModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConfirmationModalData = {} as ConfirmationModalData
    ) {
        const modalData: Partial<ConfirmationModalData> = data ?? {};

        // Determine modal type (with backward compatibility)
        if (modalData.type && CONFIRMATION_MODAL_TYPE_CONFIGS[modalData.type]) {
            // Use provided type
            this.modalType = modalData.type;
        } else if (modalData.dangerMode) {
            // Backward compatibility: dangerMode maps to DANGER type
            this.modalType = ConfirmationModalType.DANGER;
        } else {
            // Default type when no type is specified
            this.modalType = ConfirmationModalType.DEFAULT;
        }

        // Get type configuration based on modal type
        this.typeConfig = CONFIRMATION_MODAL_TYPE_CONFIGS[this.modalType];

        // Initialize properties with default values
        this.title = data.title || 'Confirmación';
        this.description = data.description || '¿Está seguro de realizar esta acción?';
        
        // descriptionColor can override type config (for backward compatibility)
        this.title = modalData.title || this.title || 'Confirmación';
        this.description = modalData.description || this.description || '¿Está seguro de realizar esta acción?';
        this.descriptionColor = modalData.descriptionColor || this.typeConfig.textColor;
        
        this.descriptionIcon = modalData.descriptionIcon;
        this.confirmButtonText = modalData.confirmButtonText || 'Confirmar';
        this.cancelButtonText = modalData.cancelButtonText || 'Cancelar';
        
        // Apply colors from type configuration
        this.buttonColor = this.typeConfig.buttonColor;
        this.iconBackgroundColor = this.typeConfig.iconBackgroundColor;
        this.iconColor = this.typeConfig.iconColor;
        this.buttonBackgroundColor = this.typeConfig.buttonBackgroundColor;
        this.buttonTextColor = this.typeConfig.buttonTextColor;
    }

    /**
     * Handles cancellation of the modal
     * Closes the modal without emitting events (returns false)
     */
    onCancel(): void {
        this.dialogRef.close(false);
    }

    /**
     * Handles confirmation of the modal
     * Emits the onConfirm event and closes the modal (returns true)
     */
    onConfirmAction(): void {
        this.onConfirm.emit();
        this.dialogRef.close(true);
    }

    /**
     * Checks whether the description icon is a string (Material icon name)
     */
    isIconString(): boolean {
        return typeof this.descriptionIcon === 'string';
    }

    /**
     * Checks whether the description icon is a TemplateRef
     */
    isIconTemplate(): boolean {
        return this.descriptionIcon instanceof TemplateRef;
    }
}
